import { Search, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SourceLinks } from '@/components/ui/source-links';
import { DiscoveryCard } from './discovery-card';
import { LITERARY_TERM_DEFINITIONS } from '@/lib/constants/literary-terms';

interface LiteraryDeviceCardProps {
    device: {
        device: string;
        category: string;
        ref?: string;
        text: string;
        explanation: string;
        sources?: Source[];
    };
    onDeviceClick?: (device: string) => void;
}

export function LiteraryDeviceCard({
    device,
    onDeviceClick
}: LiteraryDeviceCardProps) {
    const definition = LITERARY_TERM_DEFINITIONS[device.device] ||
        LITERARY_TERM_DEFINITIONS[device.device.split(' (')[0]] ||
        null;

    return (
        <DiscoveryCard
            isExpanded={true}
            onToggle={() => { }}
            canExpand={false}
            icon={Sparkles}
            header={
                <div className="flex flex-col gap-3 flex-1 min-w-0 relative z-10">
                    <div className="flex items-center justify-between gap-2">
                        <div
                            className={`flex items-center gap-1.5 ${onDeviceClick ? 'cursor-pointer hover:text-primary transition-colors group' : ''}`}
                            onClick={(e) => {
                                if (onDeviceClick) {
                                    e.stopPropagation();
                                    onDeviceClick(device.device);
                                }
                            }}
                        >
                            <h4 className="text-xl font-serif font-medium tracking-tight text-foreground/90 leading-tight">
                                {device.device}
                            </h4>
                            {onDeviceClick && (
                                <Search className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary" />
                            )}
                        </div>
                        {device.ref && (
                            <span className="text-xs font-medium text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
                                {device.ref}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[9px] font-bold uppercase tracking-widest py-0">
                                {device.category}
                            </Badge>
                        </div>
                        {definition && (
                            <p className="text-xs text-muted-foreground/80 leading-relaxed font-medium max-w-[90%]">
                                {definition}
                            </p>
                        )}
                    </div>
                </div>
            }
        >
            <div className="space-y-6 mt-1 relative z-10">
                <div className="space-y-1.5 px-1">
                    <p className="text-sm text-foreground/70 font-light leading-relaxed italic border-l-2 border-primary/20 pl-4 py-0.5">
                        "{device.text}"
                    </p>
                </div>

                <div className="space-y-1.5 px-1">
                    <p className="text-sm text-foreground/70 font-light leading-relaxed">{device.explanation}</p>
                </div>

                {device.sources?.length > 0 && (
                    <div className="border-t border-border/10">
                        <SourceLinks sources={device.sources} />
                    </div>
                )}
            </div>
        </DiscoveryCard>
    );
}
